import { useState, useEffect } from "react";
import { RotateCcw, Paintbrush, Volume2, ArrowRight } from "lucide-react";
import { useProfileContext } from "../../../context/ProfileContext";

// Drawing Success Feedback Screen
const DrawingSuccessFeedback = ({
  onDrawAgain,
  onNextWord,
}: //   userDrawing // This would be the canvas image data
any) => {
  const [showContent, setShowContent] = useState(false);
  const [showSentence, setShowSentence] = useState(false);
  const [showButtons, setShowButtons] = useState(false);

  useEffect(() => {
    const timer1 = setTimeout(() => setShowContent(true), 300);
    const timer2 = setTimeout(() => setShowSentence(true), 1000);
    const timer3 = setTimeout(() => setShowButtons(true), 1500);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  const { currentWord } = useProfileContext();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 via-blue-50 to-indigo-100 p-4">
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 max-w-lg w-full text-center border border-white/20">
        {/* Header with animated icon */}
        <div className="relative mb-6">
          <div
            className={`transform transition-all duration-1000 ${
              showContent ? "scale-100 rotate-0" : "scale-0 rotate-180"
            }`}
          >
            <div className="flex flex-col items-center justify-center gap-4 mb-4">
              <h1 className="text-3xl font-bold text-gray-800 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                Great Job
              </h1>

              <img
                src="/success.gif"
                alt="Happy Owl"
                className="w-32 h-32 rounded-full shadow-lg "
              />
            </div>
          </div>
        </div>

        {/* Success message */}
        <div
          className={`transform transition-all duration-700 delay-300 ${
            showContent
              ? "translate-y-0 opacity-100"
              : "translate-y-4 opacity-0"
          }`}
        >
          <p className="text-lg text-green-600 font-medium mb-6">
            Good try! That looks like a {currentWord!.english}
          </p>
        </div>

        {/* Sentence section */}
        <div
          className={`transform transition-all duration-700 delay-700 ${
            showSentence
              ? "translate-y-0 opacity-100"
              : "translate-y-4 opacity-0"
          }`}
        >
          <div className="bg-gradient-to-r from-blue-100 to-blue-200 rounded-2xl p-4 mb-6 border border-blue-200">
            {/* <div className="flex items-center gap-2 mb-3">
              <Info size={20} className="text-blue-600" />
              <span className="font-semibold text-blue-800">
                How to use it in a sentence?
              </span>
            </div> */}

            <p className="text-gray-700 mb-4 text-lg text-left">
              {currentWord!.sentence_en} <br />
              {currentWord!.sentence_te}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div
          className={`transform transition-all duration-700 delay-1000 ${
            showButtons
              ? "translate-y-0 opacity-100 scale-100"
              : "translate-y-4 opacity-0 scale-95"
          }`}
        >
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={onDrawAgain}
              className="group bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <Paintbrush
                size={18}
                className="group-hover:rotate-12 transition-transform duration-300"
              />
              Draw Again
            </button>

            <button
              onClick={onNextWord}
              className="group bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform duration-300"
              />
              Next Word
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Drawing Error Feedback Screen
const DrawingErrorFeedback = ({ onTryAgain, onSkip }: any) => {
  const [showContent, setShowContent] = useState(false);
  const [showButtons, setShowButtons] = useState(false);

  const { currentWord } = useProfileContext();

  useEffect(() => {
    const timer1 = setTimeout(() => setShowContent(true), 300);
    const timer2 = setTimeout(() => setShowButtons(true), 1000);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 p-4">
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 max-w-lg w-full text-center border border-white/20">
        {/* Header with animated icon */}
        <div className="relative mb-6">
          <div
            className={`transform transition-all duration-1000 ${
              showContent ? "scale-100 rotate-0" : "scale-0 -rotate-180"
            }`}
          >
            <div className="flex flex-col items-center justify-center gap-4 mb-4">
              <h1 className="text-3xl font-bold text-gray-800 bg-gradient-to-r from-red-500 to-pink-500 bg-clip-text text-transparent">
                Try Again
              </h1>
              <img
                src="/try-again.gif"
                alt="Sad Owl"
                className="w-32 h-32 rounded-full shadow-lg animate-bounce"
              />
            </div>
          </div>
        </div>

        {/* Error message */}
        <div
          className={`transform transition-all duration-700 delay-300 ${
            showContent
              ? "translate-y-0 opacity-100"
              : "translate-y-4 opacity-0"
          }`}
        >
          <p className="text-lg text-orange-600 font-medium mb-6">
            Try again. Remember what a {currentWord!.english} looks like.
          </p>
        </div>

        {/* Action buttons */}
        <div
          className={`transform transition-all duration-700 delay-700 ${
            showButtons
              ? "translate-y-0 opacity-100 scale-100"
              : "translate-y-4 opacity-0 scale-95"
          }`}
        >
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={onTryAgain}
              className="group bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-4 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <RotateCcw
                size={18}
                className="group-hover:rotate-180 transition-transform duration-500"
              />
              Try Again
            </button>

            <button
              onClick={onSkip}
              className="group bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <Volume2
                size={18}
                className="group-hover:scale-110 transition-transform duration-300"
              />
              Skip Word
            </button>
          </div>
        </div>

        {/* Background decorations */}
        <div className="absolute top-6 right-6 w-16 h-16 opacity-20">
          <div className="w-full h-full border-3 border-red-300 rounded-lg animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

type DrawingFeedbackProps = {
  isSuccess: boolean;
  onDrawAgain?: () => void;
  onNextWord?: () => void;
  onSkip?: () => void;
};

const DrawingFeedback = (props: DrawingFeedbackProps) => {
  const [currentScreen, setCurrentScreen] = useState("success");

  const { isSuccess, onDrawAgain, onNextWord, onSkip } = props;

  useEffect(() => {
    if (isSuccess) {
      setCurrentScreen("success");
    } else {
      setCurrentScreen("error");
    }
  }, [isSuccess]);

  return (
    <div>
      {/* Render Current Screen */}
      {currentScreen === "success" && (
        <DrawingSuccessFeedback
          onDrawAgain={onDrawAgain}
          onNextWord={onNextWord}
        />
      )}

      {currentScreen === "error" && (
        <DrawingErrorFeedback onTryAgain={onDrawAgain} onSkip={onSkip} />
      )}
    </div>
  );
};

export default DrawingFeedback;
